import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Building, 
  Settings, 
  Shield, 
  ScrollText, 
  Users, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building2,
  Server,
  FolderKanban,
  Lock,
  CloudSnow,
  MessageCircle,
  Brain,
  Wrench,
  Truck,
  MapPin,
  ClipboardList,
  Hash,
  Gauge,
  LayoutGrid,
  Package,
  Bot,
  TrendingUp,
  Receipt,
  Fuel
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import NotificationCenter from '../components/NotificationCenter';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Dispatch Board', href: '/dashboard/dispatch', icon: LayoutGrid, managerOnly: true },
  { name: 'Unit Numbers', href: '/dashboard/dispatch/units', icon: Truck, managerOnly: true },
  { name: 'Client Tickets', href: '/dashboard/client-tickets', icon: Receipt, managerOnly: true },
  { name: 'Clients', href: '/dashboard/clients', icon: Building, managerOnly: true },
  { name: 'Repairs', href: '/dashboard/repairs', icon: Wrench, adminOnly: true },
  { name: 'Wrench Board', href: '/dashboard/vehicles', icon: Wrench, managerOnly: true },
  { name: 'Fuel Analytics', href: '/dashboard/fuel', icon: Fuel, managerOnly: true },
  { name: 'dispatch', href: '/dashboard/dispatch/slack', icon: Hash, managerOnly: true },
  { name: 'Forms', href: '/dashboard/forms', icon: ClipboardList },
  { name: 'Parts & Inventory', href: '/dashboard/parts', icon: Package, managerOnly: true },
  { name: 'Maintenance Predictor', href: '/dashboard/maintenance-predictor', icon: TrendingUp, managerOnly: true },
  { name: 'Shop Assistant', href: '/dashboard/shop-assistant', icon: Bot, managerOnly: true },
  { name: 'Shop Orders', href: '/dashboard/shop-orders', icon: ClipboardList },
  { name: 'Departments', href: '/dashboard/departments', icon: Building },
  { name: 'Weather & Ops', href: '/dashboard/weather', icon: CloudSnow },
  { name: 'WhatsApp', href: '/dashboard/whatsapp', icon: MessageCircle, managerOnly: true },
  { name: 'Field Marshal', href: '/dashboard/fieldmarshal', icon: Wrench, managerOnly: true },
  { name: 'Fleetio', href: '/dashboard/fleetio', icon: Truck, managerOnly: true },
  { name: 'Titan GPS', href: '/dashboard/titan-gps', icon: MapPin, managerOnly: true },
  { name: '400Hr Service', href: '/dashboard/service-tracking', icon: Gauge, managerOnly: true },
  { name: 'Learning', href: '/dashboard/learning', icon: Brain, managerOnly: true },
  { name: 'Privacy Dashboard', href: '/dashboard/privacy', icon: Shield, managerOnly: true },
  { name: 'Audit Logs', href: '/dashboard/audit-logs', icon: ScrollText, managerOnly: true },
  { name: 'Users', href: '/dashboard/users', icon: Users, adminOnly: true },
  { name: 'Ollama Setup', href: '/dashboard/ollama-guide', icon: Server, adminOnly: true },
  { name: 'Settings', href: '/dashboard/settings', icon: Settings, adminOnly: true },
];

export default function DashboardLayout() {
  const { user, tenant, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Poll for in-app notifications (e.g. time-off approve/deny) and toast once
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('vaultai_token');
        if (!token) return;
        const res = await axios.get(`${API}/forms/user-notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = res.data?.notifications || [];
        if (!alive || list.length === 0) return;
        const ids = [];
        list.forEach(n => {
          ids.push(n.id);
          if (n.level === 'warning') {
            toast.warning(n.title, { description: n.message });
          } else if (n.level === 'error') {
            toast.error(n.title, { description: n.message });
          } else {
            toast.success(n.title, { description: n.message });
          }
        });
        await axios.post(`${API}/forms/user-notifications/mark-read`,
          { ids },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        // Silent — notifications are best-effort
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60 * 1000);
    return () => { alive = false; clearInterval(interval); };
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const filteredNav = navigation.filter(item => {
    if (item.adminOnly) return user?.role === 'admin';
    if (item.managerOnly) return user?.role === 'admin' || user?.role === 'manager';
    return true;
  });

  // Get department access info
  const departmentCount = user?.department_ids?.length || 0;
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <img 
                src="/rogers-logo.png" 
                alt="Rogers Trucking" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-xl font-bold text-foreground">Rogers OPS</span>
            </Link>
          </div>

          {/* Department Access Indicator */}
          <div className="px-4 py-3 border-b border-border">
            <div className="px-3 py-2 rounded-lg text-sm bg-primary/10 border border-primary/30 text-primary">
              <div className="flex items-center gap-2">
                <FolderKanban className="w-4 h-4" />
                <span className="font-medium">
                  {(isAdmin || isManager) ? "All Departments" : `${departmentCount} Department${departmentCount !== 1 ? 's' : ''}`}
                </span>
              </div>
              <p className="text-xs mt-1 opacity-80">
                {(isAdmin || isManager)
                  ? "Full access to all documents" 
                  : departmentCount > 0 
                    ? "Access based on your assignments"
                    : "No departments assigned yet"
                }
              </p>
            </div>
          </div>

          {/* Privacy Mode Indicator */}
          {tenant && (
            <div className="px-4 py-3 border-b border-border">
              <div className={cn(
                "px-3 py-2 rounded-lg text-sm",
                tenant.default_llm_provider === 'openai' || tenant.default_embeddings_provider === 'openai'
                  ? "bg-amber-500/10 border border-amber-500/30 text-amber-400"
                  : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"
              )}>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span className="font-medium">
                    {tenant.default_llm_provider === 'openai' || tenant.default_embeddings_provider === 'openai'
                      ? "Cloud Mode"
                      : "Private Mode"
                    }
                  </span>
                </div>
                <p className="text-xs mt-1 opacity-80">
                  {tenant.default_llm_provider === 'openai' || tenant.default_embeddings_provider === 'openai'
                    ? "AI powered by OpenAI"
                    : "No third-party data transfer"
                  }
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    active 
                      ? "bg-primary/10 text-primary" 
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  {item.name}
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Desktop header with notifications */}
        <header className="hidden lg:flex sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-end w-full px-6 py-3">
            <NotificationCenter />
          </div>
        </header>

        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 glass border-b border-border">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-secondary"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="flex items-center gap-2">
              <img src="/rogers-logo.png" alt="Rogers" className="w-8 h-8 object-contain" />
              <span className="font-bold">Rogers OPS</span>
            </Link>
            <NotificationCenter />
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>

      {/* Mobile close button */}
      {sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          className="fixed top-4 right-4 z-50 lg:hidden p-2 rounded-lg bg-card border border-border"
        >
          <X className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
